import React from "react";
import {CheckCircleFilled, LoadingOutlined} from "@ant-design/icons";
import {Button, Text, TextAs, TextSizes, ButtonHierarchies} from "@clockout-dev/design-elements";
import {MODES, RUN_STAGES, VERIFY_LATENCY_NOTE} from "../uploads-simulator.constants";
import styles from "../styles/results.module.scss";

function formatElapsed(elapsedMs) {
    const totalSeconds = Math.max(0, Math.floor((elapsedMs || 0) / 1000));
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
}

/**
 * Live run progress: one row per stage (upload with the real percentage from
 * `onUploadProgress`, then queueing, AI verification with the elapsed clock,
 * then report compilation) plus the cancel affordance. Cancelling only stops
 * the client — the server may still finish, but simulations persist nothing.
 *
 * @param {Object} props
 * @param {Object} props.run — controller run state ({stage, uploadPct, elapsedMs, mode, fileCount})
 * @param {() => void} props.onCancel
 */
function RunProgress({run, onCancel}) {
    const activeIndex = Math.max(
        0,
        RUN_STAGES.findIndex(stage => stage.key === run?.stage)
    );
    const modeLabel = MODES[run?.mode]?.label || run?.mode || "";

    return (
        <div className={styles.progressCard}>
            <div className={styles.progressHeader}>
                <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.progressTitle}>
                    Running {modeLabel} simulation
                </Text>
                <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.progressClock}>
                    {formatElapsed(run?.elapsedMs)}
                </Text>
            </div>

            <div className={styles.stageList}>
                {RUN_STAGES.map((stage, index) => {
                    const state = index < activeIndex ? "done" : index === activeIndex ? "active" : "pending";
                    const detail =
                        stage.key === "upload" && state !== "pending"
                            ? `${Math.min(run?.uploadPct ?? 0, 100)}%`
                            : stage.key === "verify" && state === "active"
                              ? `${run?.fileCount || 1} document${(run?.fileCount || 1) === 1 ? "" : "s"}`
                              : null;

                    return (
                        <div key={stage.key} className={`${styles.stageRow} ${styles[`stage_${state}`]}`}>
                            <span className={styles.stageIcon}>{state === "done" ? <CheckCircleFilled /> : state === "active" ? <LoadingOutlined spin /> : <span className={styles.stageDot} />}</span>
                            <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.stageLabel}>
                                {stage.label}
                            </Text>
                            {detail ? (
                                <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.stageDetail}>
                                    {detail}
                                </Text>
                            ) : null}
                        </div>
                    );
                })}
            </div>

            <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.latencyNote}>
                {VERIFY_LATENCY_NOTE}
            </Text>

            <Button hierarchy={ButtonHierarchies.SECONDARY} destructive className={styles.cancelButton} onClick={onCancel}>
                Cancel run
            </Button>
            <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.cancelNote}>
                Cancelling stops polling — the server may still finish, but simulations never persist anything.
            </Text>
        </div>
    );
}

export default RunProgress;
